import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="relative">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-200"></div>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent absolute top-0 left-0"></div>
      </div>
    </div>
  );
}

// OptimizedMedia: ONLY for image/video rendering
const OptimizedMedia = React.memo(({ src, alt, className, onLoad, loading = "lazy", mediaType = "auto" }) => {
  const { t } = useTranslation('PropertyCard');
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const handleMediaLoad = useCallback(() => {
    setMediaLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleMediaError = useCallback(() => {
    setMediaError(true);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  }, [isPlaying]);

  const detectedMediaType = useMemo(() => {
    if (mediaType !== "auto") return mediaType;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
    return videoExtensions.some(ext => src?.toLowerCase().includes(ext)) ? 'video' : 'image';
  }, [src, mediaType]);

  if (mediaError || !src) {
    return (
      <div className={`bg-gray-200 dark:bg-gray-700 flex items-center justify-center ${className}`}>
        <span className="text-gray-400">{t('propertyCard.media.notAvailable')}</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {!mediaLoaded && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
      
      {detectedMediaType === 'video' ? (
        <>
          <video
            ref={videoRef}
            src={src}
            className={`w-full h-full object-cover transition-opacity duration-500 ${
              mediaLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoadedData={handleMediaLoad}
            onError={handleMediaError}
            loop
            muted
            playsInline
            preload={loading === "eager" ? "auto" : "metadata"}
          />
          
          <div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            onClick={togglePlayPause}
          >
            {!isPlaying && mediaLoaded && (
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 hover:bg-black/70">
                <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            )}
          </div>
          
          <div className="absolute bottom-2 right-2">
            <div className="w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
              </svg>
            </div>
          </div>
        </>
      ) : (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            mediaLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading={loading}
          onLoad={handleMediaLoad}
          onError={handleMediaError}
        />
      )}
    </div>
  );
});




export default function PropertyCard({ property, isActive, isScrolling, loading = "lazy", onImageLoad, onClick, fxRates, userCurrency }) {
  const { t } = useTranslation('PropertyCard');
  const isVideo = !!property.video;
  const mainMediaUrl = property.video ? property.video : property.images?.[0]?.image;
  const isAvailable = !!property.video || (Array.isArray(property.images) && property.images.length > 0);
  
  const convertedPrice = (originalPriceUSD) => {
    if (!fxRates || !fxRates[userCurrency]) return 'N/A';
    const rate = fxRates[userCurrency];
    return (originalPriceUSD * rate).toLocaleString(undefined, {
      style: 'currency',
      currency: userCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  return (
    <div className={`relative w-full h-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl 2xl:max-w-3xl mx-auto  transition-all duration-700 ease-out ${
      isActive 
        ? 'scale-100 opacity-100' 
        : 'scale-95 opacity-75'
    } ${isScrolling ? 'pointer-events-none' : ''} cursor-pointer`} onClick={onClick}>
      <div className="relative w-full h-full rounded-3xl sm:rounded-[2rem] md:rounded-[2.5rem] overflow-hidden shadow-2xl bg-[var(--bg-black)]">
        
        {/* Main Media */}
        <div className="relative w-full h-full">
          <OptimizedMedia
            src={mainMediaUrl}
            alt={property.title}
            className="w-full h-full"
            loading={loading}
            mediaType={isVideo ? 'video' : 'image'}
            onLoad={() => onImageLoad?.(property.id)}
          />
          
          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent" />
          
          {/* Top Bar */}
          <div className="absolute top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 flex items-center justify-between text-white z-10">
            <div className="flex items-center gap-2 bg-black/30 backdrop-blur-md px-3 py-2 rounded-full">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">{property.location}</span>
            </div>
          </div>
          
          {/* Bottom Content */}
          <div className="absolute bottom-6 sm:bottom-8 left-4 sm:left-6 right-4 sm:right-6 text-[var(--text-black)] flex flex-col">
            <div
              className="text-sm sm:text-base leading-relaxed mb-3 pr-1 overflow-y-auto scrollbar-hide"
              style={{
                maxHeight: '2.75rem',
                lineHeight: '1.25rem',
              }}
            >
              {property.description}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className="bg-white/40 px-4 py-2.5 rounded-full shadow-lg">
                  {fxRates && userCurrency ? (
                    convertedPrice(property.price_usd || 0)
                  ) : property.price_usd ? (
                    `$${property.price_usd.toLocaleString()}`
                  ) : (
                    t('propertyCard.price', { price: 'N/A' })
                  )}
                </div>
                
                <div className="bg-white/40 backdrop-blur-md px-3 py-2 rounded-full text-[var(--text-black)]">
                  <span className="text-sm font-medium">
                    {isAvailable 
                      ? t('propertyCard.status.available') 
                      : t('propertyCard.status.notAvailable')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Video Sound Indicator */}
          {property.video && (
            <div className="absolute bottom-4 right-4 sm:right-6">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center animate-spin-slow">
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-[var(--text-black)]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}